import { cls } from "cls";
import React, { useEffect, useState } from "react";
import { Button } from "@clarkinc/zest-design-system/ui";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { ButtonProps } from "@clarkinc/zest-design-system";
import { PlusStatus } from "./plusStatus";
import connectUserAndChat, { User } from "~/common/Store/connectUserAndChat";
import fetchCurrentUserPlusStatus from "./fetchCurrentUserPlusStatus";
import tracking from "~/Gtm/Tracking/tracking";
import useResizeObserver from "../../../ProductDetails/VirtualGrouping/Hooks/useResizeObserver";
import fetchPlusModalDataExtended from "~/common/PlusSubscriptionModal/fetchPlusModalDataExtended";
import getAdBackgroundStyles from "./getAdBackgroundStyles";
import { plusLogoLink } from "~/common/Constants/links";
import determineWebPlusAdVisibility from "./determineWebPlusAdVisibility";
interface WebPlusShippingAdProps {
  page: string;
  mounted?: boolean;
  user?: User;
  containerClass?: string;
  headerClassCss?: string;
  subheaderClassCss?: string;
}
const webPlusShippingThreshold = 999.99;
const WebPlusShippingAd = connectUserAndChat(({
  mounted,
  page,
  user,
  containerClass,
  headerClassCss,
  subheaderClassCss
}: WebPlusShippingAdProps) => {
  const [plusStatusData, setPlusStatusData] = useState<PlusStatus | null>(null);
  const [screenSize, setScreenSize] = useState("mobile");
  const [isFreeTrialAvailable, setIsFreeTrialAvailable] = useState(false);
  const [buttonSize, setButtonSize] = useState<ButtonProps["size"] | undefined>();
  const isHomepage = page.toLowerCase() === "homepage";
  useEffect(() => {
    const getCurrentUserPlusStatus = async () => {
      const maybePlusStatusData = await fetchCurrentUserPlusStatus();
      if (maybePlusStatusData.isError) return;
      const plusStatusData = maybePlusStatusData.value;
      setPlusStatusData(plusStatusData);
    };
    const getPlusData = async () => {
      const data = await fetchPlusModalDataExtended();
      if (data.isError) return;
      setIsFreeTrialAvailable(data.value.isFreeTrialAvailable);
    };
    getPlusData();
    getCurrentUserPlusStatus();
  }, []);
  const {
    width
  } = useResizeObserver();
  useEffect(() => {
    const updateScreenSize = () => {
      if (width < 640) {
        setScreenSize("mobile");
        setButtonSize("small");
      } else if (width >= 640 && width < 1024) {
        setScreenSize("tablet");
        setButtonSize("medium");
      } else if (width >= 1440 && isHomepage) {
        setScreenSize("desktop");
        setButtonSize("large");
      } else {
        setScreenSize("desktop");
        setButtonSize("medium");
      }
    };
    updateScreenSize();
  }, [width, isHomepage]);
  const isPlusMember = plusStatusData?.isPlusMember ?? false;
  const freeviewPromotionNumber = user?.freeviewPromotion ?? 0;
  const actualSavings = plusStatusData?.actualSavings ?? 0;
  const theoreticalSavings = plusStatusData?.theoreticalSavings ?? 0;
  const formattedActualSavings = formatAsCurrency.formatIfUndefined(actualSavings, false, false, {
    maximumFractionDigits: 0
  });
  const formattedTheoreticalSavings = formatAsCurrency.formatIfUndefined(theoreticalSavings, false, false, {
    maximumFractionDigits: 0
  });
  const estimatedSubCost = plusStatusData?.estimatedSubscriptionCost ?? 0;
  const userHasNoSavings = actualSavings + theoreticalSavings === 0;
  const nonPlusLowTheoreticalSavings = !isPlusMember && theoreticalSavings <= webPlusShippingThreshold;
  const nonPlusHighTheoreticalSavings = !isPlusMember && theoreticalSavings > webPlusShippingThreshold;
  const plusUserSavingMoreThanSubCosts = isPlusMember && actualSavings > estimatedSubCost;
  const plusUserTheoreticalLowSavings = isPlusMember && theoreticalSavings <= webPlusShippingThreshold;
  let headerText = "";
  if (nonPlusLowTheoreticalSavings) {
    headerText = `Unlock FREE & Priority Shipping with Plus!`;
  } else if (plusUserSavingMoreThanSubCosts) {
    headerText = `You saved ${formattedActualSavings} in the last 12 months with Plus!`;
  } else {
    headerText = `You could have saved ${formattedTheoreticalSavings} in the last 12 months with Plus!`;
  }
  let subheaderText = "";
  if (plusUserSavingMoreThanSubCosts) {
    subheaderText = "Congratulations! Your Plus membership has paid for itself.";
  } else {
    subheaderText = "Get free shipping, member discounts, guaranteed savings, & more.";
  }
  let plusAdId = "";
  if (!isPlusMember) {
    plusAdId = theoreticalSavings <= webPlusShippingThreshold ? `${page}-nonplus-underspent` : `${page}-nonplus-overspent`;
  } else {
    plusAdId = isHomepage ? "homepage-plus-savings-met" : `${page}-plus-info`;
  }
  const homePageLinkContainerPadding = `box-border px-4 md:px-5 py-4 sm:py-5 lg:py-4 xxl:py-[32px] ${isFreeTrialAvailable ? "max-h-[145px] pr-[139px] sm:pr-48 md:pr-[200px]" : ""}`;
  const getAdContainerId = () => {
    if (isHomepage) return "cio-plusday-plusad";
    if (userHasNoSavings || plusUserTheoreticalLowSavings) {
      return "myaccount-none";
    }
    return "myaccount-cio-plusday-plusad";
  };
  let dataTrackLabel = "generic";
  let dataTrackEi = "";
  if (isPlusMember) {
    dataTrackLabel = "actual";
    dataTrackEi = "web-plus-plus-saved-more";
  } else if (nonPlusHighTheoreticalSavings) {
    dataTrackLabel = "theoretical";
    dataTrackEi = isFreeTrialAvailable ? "web-plus-non-plus-saved-more-free-trial" : "web-plus-non-plus-saved-more-no-free-trial";
  } else if (nonPlusLowTheoreticalSavings) {
    dataTrackLabel = "theoretical";
    dataTrackEi = isFreeTrialAvailable ? "web-plus-non-plus-saved-less-free-trial" : "web-plus-non-plus-saved-less-no-free-trial";
  }
  const shouldShowAd = determineWebPlusAdVisibility(plusStatusData, freeviewPromotionNumber);
  return <div id={getAdContainerId()}>
        {mounted && shouldShowAd && <div className={cls("account__section", "account__section--dashboard", "border-box", "flex", "mt-0", "pb-px", "md:min-h-[96px]", plusUserSavingMoreThanSubCosts ? "" : "md:max-h-60", containerClass)} data-testid={`${dataTrackLabel}-ad`}>
            <tracking.a data-track={`web-plus-${page}-ad`} data-track-ei={dataTrackEi} data-module="initWebPlusAd" href="/plus/" className={cls("border-none", "flex", isHomepage ? "flex-col min-h-[100px]" : "flex-row px-3 py-3 sm:py-4", "sm:flex-row", isHomepage && homePageLinkContainerPadding, "rounded", "w-full", "sm:items-center", "md:justify-between", !isHomepage && screenSize === "mobile" && "min-h-[86px]", isPlusMember && !isHomepage && "md:h-[70px]", nonPlusLowTheoreticalSavings && !isHomepage && isFreeTrialAvailable && "md:h-[100px] lg:h-[90px] xxl:h-[70px]", nonPlusLowTheoreticalSavings && !isHomepage && !isFreeTrialAvailable && "md:h-[100px] lg:h-[90px] xl:h-[80px]", nonPlusHighTheoreticalSavings && !isHomepage && "lg:h-[100px] xl:h-[70px]", `${page}-plus-ad`)} style={getAdBackgroundStyles(screenSize, (nonPlusLowTheoreticalSavings || nonPlusHighTheoreticalSavings) && isFreeTrialAvailable)} id={plusAdId}>
              <div className={cls("flex", "flex-shrink-0", "items-center", isHomepage && "mr-4 lg:mr-5 my-0 pb-2 sm:pb-0 justify-left sm:justify-center")}>
                <img alt="Plus Logo" className={cls("h-5", "sm:h-8", "max-w-full", "align-middle", isHomepage ? "lg:h-10 xxl:h-[50px]" : "pr-2 sm:pr-3")} src={plusLogoLink} />
              </div>
              <div className={cls("flex", "flex-col", "md:grow", "md:flex-row", "md:justify-between", "md:items-center", nonPlusLowTheoreticalSavings && !isHomepage && isFreeTrialAvailable && "self-center")}>
                <div className={cls(plusUserSavingMoreThanSubCosts && isHomepage && "mb-0", !isHomepage && !isPlusMember && isFreeTrialAvailable && "mr-40 md:mr-36 lg:mr-44", !isHomepage && nonPlusHighTheoreticalSavings && isFreeTrialAvailable && "mr-[115px] xxm:mr-[155px] md:mr-44")}>
                  <h4 className={cls(headerClassCss, !isHomepage && isFreeTrialAvailable && nonPlusHighTheoreticalSavings && "sm:text-base")}>
                    {headerText}
                  </h4>
                  <p className={cls(subheaderClassCss, !isHomepage && !isFreeTrialAvailable && !isPlusMember && "mb-2", !isHomepage && isFreeTrialAvailable && nonPlusHighTheoreticalSavings && "sm:text-xs")}>
                    {subheaderText}
                  </p>
                </div>
                {!isFreeTrialAvailable && !plusUserSavingMoreThanSubCosts && <div className={cls(plusUserSavingMoreThanSubCosts && "hidden", "md:flex", "md:flex-col", isHomepage && "md:ml-22 lg:ml-14 mt-[5px] sm:mt-[9px] md:mt-0")}>
                    <div className={isHomepage ? "relative" : "md:flex md:justify-between md:relative"} />
                    <Button theme="wss" color="blue" size={buttonSize} className="md:whitespace-nowrap">
                      Get Free Shipping
                    </Button>
                    <div className="md:flex md:justify-between relative" />
                  </div>}
              </div>
            </tracking.a>
          </div>}
      </div>;
});
export default WebPlusShippingAd;